import { useEffect, useState } from 'react';

interface Props {
    detectCondition?: boolean; // use detectCondition if used within a component that is conditionally rendered (i.e. isOpen in HBDrawer or popovers)
    elementRef: React.RefObject<HTMLElement> | HTMLElement;
    direction?: 'vertical' | 'horizontal';
    spaceWhenNotScrolled?: number;
}

export const useDetectScroll = ({
    elementRef,
    direction = 'vertical',
    detectCondition = true,
    spaceWhenNotScrolled = 0
}: Props) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

    useEffect(() => {
        if (!elementRef || !detectCondition) {
            return;
        }
        const isRef = 'current' in elementRef;
        const element = isRef ? elementRef.current : elementRef;

        const [axis, dimension] = DIRECTION_DEFINITIONS[direction];

        if (element) {
            function handleScroll() {
                const scrollPositionToCeil = Math.ceil(
                    element[`scroll${axis}`]
                );
                setIsScrolled(scrollPositionToCeil > 0);
                setIsScrolledToEnd(
                    element[`scroll${dimension}`] -
                        scrollPositionToCeil -
                        spaceWhenNotScrolled <=
                        element[`client${dimension}`]
                );
            }

            handleScroll();

            const resizeObserver = new ResizeObserver(handleScroll);
            resizeObserver.observe(element);

            const mutationObserver = new MutationObserver(handleScroll);
            mutationObserver.observe(element, {
                subtree: true,
                childList: true,
                attributes: true
            });

            const controller = new AbortController();
            element.addEventListener('scroll', handleScroll, {
                signal: controller.signal
            });
            return () => {
                controller.abort();
                resizeObserver.disconnect();
                mutationObserver.disconnect();
            };
        }
    }, [elementRef, detectCondition, direction, spaceWhenNotScrolled]);

    return { isScrolled, isScrolledToEnd };
};

const DIRECTION_DEFINITIONS = {
    vertical: ['Top', 'Height'],
    horizontal: ['Left', 'Width']
} as const;
